import React from 'react';
import {
  X, MapPin, Clock, Briefcase, Users, Building2,
  HelpCircle, Eye, CheckCircle, Image as ImageIcon, FileText
} from 'lucide-react';
import { timeAgo } from '../../../utils/timeAgo';
import { useAuth } from '../../../contexts/AuthContext';

const TYPE_COLORS = {
  'Full Time':  { bg: 'bg-[#e8fbf2]', text: 'text-[#10b981]' },
  'Part Time':  { bg: 'bg-blue-50',   text: 'text-blue-600'  },
  'Internship': { bg: 'bg-purple-50', text: 'text-purple-600' },
  'Contract':   { bg: 'bg-orange-50', text: 'text-orange-600' },
};

const MetaCard = ({ icon: Icon, label, value }) => (
  <div className="flex flex-col gap-2 bg-gray-50 rounded-2xl p-4 border border-gray-100">
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm flex-shrink-0">
        <Icon size={15} className="text-[#6366f1]" />
      </div>
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-sm font-bold text-gray-900 pl-1 leading-snug break-words [overflow-wrap:anywhere]">{value}</p>
  </div>
);

const OpportunityDetailModal = ({ isOpen, onClose, opportunity, userRole, onApply }) => {
  const { user: currentUser } = useAuth();
  
  if (!isOpen || !opportunity) return null;

  const {
    title, clinicName, location, type, experienceRequired,
    screeningQuestion, caseAttachmentUrl, isOpen: isOpportunityOpen, views,
    createdAt, _count, hasApplied, postedById
  } = opportunity;

  const isAuthor = currentUser?.id === postedById;

  const colors = TYPE_COLORS[type] || { bg: 'bg-gray-100', text: 'text-gray-700' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal panel */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto scrollbar-hide animate-fadeIn">

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="p-6 pb-4">
          {/* Badge row + close */}
          <div className="flex items-center justify-between mb-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${colors.bg} ${colors.text}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
              {type}
            </span>
            <button
              id="close-detail-modal"
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-extrabold text-gray-900 leading-tight mb-2 break-words [overflow-wrap:anywhere]">
            {title}
          </h2>

          {/* Clinic */}
          <div className="flex items-center gap-1.5 text-sm text-gray-500 font-medium">
            <Building2 size={14} className="text-gray-400 flex-shrink-0" />
            <span className="break-words [overflow-wrap:anywhere]">{clinicName}</span>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className="px-6 pb-6 space-y-4">

          {/* 2×2 Meta grid */}
          <div className="grid grid-cols-2 gap-3">
            <MetaCard icon={MapPin}   label="Location"    value={location} />
            <MetaCard icon={Briefcase} label="Experience"  value={experienceRequired} />
            <MetaCard icon={Users}    label="Applicants"  value={`${_count?.applications ?? 0} candidates`} />
            <MetaCard icon={Eye}      label="Views"        value={`${views ?? 0} views`} />
          </div>

          {/* Posted time */}
          <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
            <Clock size={12} />
            <span>Posted {timeAgo(createdAt)}</span>
            {!isOpportunityOpen && (
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-semibold">
                Closed
              </span>
            )}
          </div>

          {/* Clinical Screening Question */}
          {screeningQuestion && (
            <div className="bg-[#f5f3ff] border border-purple-100 rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0">
                  <HelpCircle size={13} className="text-purple-700" />
                </div>
                <span className="text-sm font-bold text-purple-700">Clinical Screening Question</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed pl-8 break-words [overflow-wrap:anywhere]">{screeningQuestion}</p>

              {/* Attached Clinical Case Image / Document */}
              {caseAttachmentUrl && (
                <div className="pl-8 pt-2">
                  <div className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1.5">
                    <ImageIcon size={14} /> Attached Clinical Case
                  </div>
                  {/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i.test(caseAttachmentUrl) ? (
                    <div className="relative group rounded-xl overflow-hidden border border-purple-200 bg-white max-h-80 max-w-md">
                      <img 
                        src={caseAttachmentUrl} 
                        alt="Attached Clinical Case" 
                        className="w-full h-full object-cover max-h-80 cursor-pointer transition-transform duration-300 group-hover:scale-105" 
                        onClick={() => window.open(caseAttachmentUrl, '_blank')}
                      />
                    </div>
                  ) : (
                    <a 
                      href={caseAttachmentUrl} 
                      target="_blank" 
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-xs font-semibold text-purple-700 bg-white px-3.5 py-2 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors shadow-xs"
                    >
                      <FileText size={16} /> View Case Attachment ↗
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer CTA ─────────────────────────────────────────── */}
        <div className="px-6 pb-6">
          {isAuthor ? (
            <div className="flex items-center justify-center gap-2 py-3 px-4 bg-[#f8fafc] text-gray-500 rounded-2xl text-sm font-semibold border border-gray-200">
              <Briefcase size={16} />
              This is your posting
            </div>
          ) : hasApplied ? (
            <div className="flex items-center justify-center gap-2 py-3 px-4 bg-green-50 text-green-700 rounded-2xl text-sm font-semibold border border-green-100">
              <CheckCircle size={16} />
              You've already applied for this position
            </div>
          ) : isOpportunityOpen ? (
            <button
              id="apply-from-detail"
              onClick={() => { onClose(); onApply(opportunity); }}
              className="w-full py-3.5 text-sm font-bold text-white bg-[#6366f1] hover:bg-[#4f46e5] rounded-2xl transition-colors duration-200 shadow-sm shadow-indigo-200"
            >
              Apply Now
            </button>
          ) : (
            <div className="text-center py-3 text-sm text-red-500 font-medium">
              This position is no longer accepting applications
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetailModal;
